import { PROJECT_TABS } from "./project-tabs"
import type { PropertyTabKey } from "./property-tabs"
import { PROPERTY_TABS } from "./property-tabs"

/**
 * Route → rail-context mapping for the desktop rail's section navigation
 * (ZPL-352). Pure so the route→active-item contract is unit-testable
 * (the design's rail indicates the CURRENT section; a wrong mapping here
 * silently mis-highlights on deep links).
 */

interface RailContext {
	propertyId: string | null
	projectId: string | null
}

const PROPERTY_RE = /^\/properties\/([^/]+)/
const PROJECT_RE = /^\/properties\/[^/]+\/projects\/([^/]+)/

export function railContextOf(pathname: string): RailContext {
	const propertyMatch = PROPERTY_RE.exec(pathname)
	const propertyId = propertyMatch?.[1] ?? null
	if (!propertyId || propertyId === "new") {
		return { propertyId: null, projectId: null }
	}
	const projectMatch = PROJECT_RE.exec(pathname)
	const projectId = projectMatch?.[1] ?? null
	return { propertyId, projectId: projectId === "new" ? null : projectId }
}

export type ProjectTabKey = (typeof PROJECT_TABS)[number]["key"]

const PROPERTY_KEYS = new Set<string>(PROPERTY_TABS.map((t) => t.key))
const PROJECT_KEYS = new Set<string>(PROJECT_TABS.map((t) => t.key))

export function activePropertyTabKey(
	pathname: string,
	propertyId: string,
): PropertyTabKey | null {
	const rest = pathname.slice(`/properties/${propertyId}`.length)
	if (rest === "" || rest === "/") return "overview"
	const segment = rest.split("/")[1]
	if (segment === "projects") return "projects"
	return PROPERTY_KEYS.has(segment) ? (segment as PropertyTabKey) : null
}

export function activeProjectTabKey(
	pathname: string,
	propertyId: string,
	projectId: string,
): ProjectTabKey | null {
	const rest = pathname.slice(
		`/properties/${propertyId}/projects/${projectId}`.length,
	)
	if (rest === "" || rest === "/") return "details"
	const segment = rest.split("/")[1]
	// app.tsx renders the project journal route on the details tab.
	if (segment === "journal") return "details"
	return PROJECT_KEYS.has(segment) ? (segment as ProjectTabKey) : null
}
